import * as React from "react";
import {
  Box,
  Card,
  Typography,
  Divider,
  ButtonBase,
  useTheme,
  Avatar,
  Stack,
  CircularProgress,
  Collapse,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchData } from "assets/utils";
import SeriesIcon from "components/SeriesIcon";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from '@mui/icons-material/Person';
import { disp_rank_short } from "assets/utils";
import { BoxTitle, PlayerName, AccentBar, ActivityTab, H2HHeader, H2HFullSummary } from "./ActivityCard";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { WinIcon } from "assets/Icons";
import { LoseIcon } from "assets/Icons";
import { act } from "react";
import { useHead2Head } from "assets/utils";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ShadowBox from "components/ShadowBox";
import OutlinedBox from "components/OutlinedBox";
import { useTranslation } from "react-i18next";
import { formatDateDifference } from "assets/utils";

function MoreButton({ onClick }) {
  const { t } = useTranslation();
  return <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <ButtonBase
        onClick={onClick}
        sx={{ 
          display: "flex", justifyContent: "center", borderRadius: 2, backgroundColor: (theme) => theme.palette.primary.main,
          borderRadius: 2,
          px: 1.5,
          py: 1,
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
        }}
      >
        <FormatListBulletedIcon sx={{ fontSize: 18, color: 'white' }} />
        <Typography sx={{ fontSize: 12, color: 'white', fontWeight: 600}}>
          {t("recentActivity.moreMatches")}
        </Typography>
      </ButtonBase>
    </Box>
}

export default function RecentActivityCard({ activity, name, rank }) {
  const { t } = useTranslation();
  const padelTournamentActivity = activity?.padel_tn ?? [];
  const padelInterclubActivity = activity?.padel_ic ?? [];

  const STEP = 4;

  //const [visibleInterclubPadel, setVisibleInterclubPadel] = React.useState(STEP);

  /*React.useEffect(() => {
      if (padelTournamentActivity.length > 0) {
        setValue(0);
      } else if (padelInterclubActivity.length > 0) {
        setValue(1);
      } else {
        setValue(0); // default to singles tab if no activity, but could be anything since both are empty
      }
    }, [activity]);*/

  //const shownTournamentPadel = padelTournamentActivity.slice(0, visibleTournamentPadel);
  //const shownInterclubPadel = padelInterclubActivity.slice(0, visibleInterclubPadel);

  //const canShowMoreTournamentPadel = visibleTournamentPadel < padelTournamentActivity.length;
  //const canShowMoreInterclubPadel = visibleInterclubPadel < padelInterclubActivity.length;


  const activeActivity = [...padelTournamentActivity, ...padelInterclubActivity].sort((a, b) => new Date(b.date) - new Date(a.date));

  //const [value, setValue] = React.useState(0);
  //const activeActivity = value === 0 ? padelTournamentActivity : padelInterclubActivity;
  const winRatio = activeActivity && activeActivity.length > 0 ? activeActivity.filter(a => a.m_score?.winner === 0).length / activeActivity.length : null;

  return (
    <>

      {/*<ActivityTab labels={["Tornooien (" + padelTournamentActivity.length + ")", "Interclub (" + padelInterclubActivity.length + ")"]} value={value} setValue={setValue} />*/}

      {activeActivity.length > 0 ?
      <ShadowBox sx={{py: 1, px: 1, display: "flex", textAlign: "center", justifyContent: "space-evenly", flexDirection: "row", mb: 1}}>
        <Box sx={{width: "50%"}}>
          <Typography variant="body2" color="text.secondary">
            {t("recentActivity.winRatio")}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="text.primary_bold" component="div">
            {winRatio !== null ? `${Math.round(winRatio * 100)}%` : "-"}
          </Typography>
        </Box>
         <Box sx={{width: "50%"}}>
          <Typography variant="body2" color="text.secondary">
            {activeActivity.length > 1 ? t("recentActivity.last", {count: Math.min(activeActivity.length, 5)}) : t("recentActivity.lastNoCount")}
          </Typography>
          <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", mt: 0.5}}>
            {activeActivity.slice(0, 5).map((a, index) => a.m_score?.winner === 0 ? <WinIcon key={a.mid + a.sid + index} /> : <LoseIcon key={a.mid + a.sid + index} />)}
          </Box>
        </Box>
      </ShadowBox> : null}

      <RecentActivityList activity={activeActivity} firstPlayerClickable={false} STEP={STEP} />

    <OutlinedBox sx={{py: 1, px: 1, fontStyle: "italic", textAlign: "center", display: activeActivity.length > 0 ? "none" : "block" }}>
      <Typography variant="body2" color="text.secondary">
        {t("recentActivity.noRecent")}
      </Typography>      
    </OutlinedBox>
    </>
  );
}

export function RecentActivityList({activity, STEP=4, firstPlayerClickable = false}) {
  const [visibleCount, setVisibleCount] = React.useState(STEP);

  React.useEffect(() => {
    setVisibleCount(STEP);
  }, [activity]);

  const shownItems = activity.slice(0, visibleCount);

  const canShowMoreItems = visibleCount < activity.length;

  return activity.length > 0 && <Stack sx={{ gap: 1 }}>
        {shownItems.map((item, index) => (
          <React.Fragment key={`${item.sid}-${index}`}>
            <RecentActivityLine
              data={item}
              doubles={item.p_id}
              firstPlayerClickable={firstPlayerClickable}
            />
            {/*index < shownSingles.length - 1 && <Divider />*/}
          </React.Fragment>
        ))}

        {canShowMoreItems && (
          <MoreButton onClick={() => setVisibleCount((prev) => prev + STEP)} />
        )}
        </Stack>
}

function SetItemElement({ winner, score1, score2, tiebreak, t1Color, t2Color, bold = true, gap='4px' }) {
  const theme = useTheme();

  const tiebreakElement = <b style={{color: '#999', position: "absolute", marginTop: "-10px", fontSize: 10, fontWeight: 500}}>{tiebreak}</b>

  return <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: gap, width: "20px"}}>
    <b style={{color: score1 > score2 ? t1Color : "#aaa", fontWeight: bold && score1 > score2 ? "normal" : "normal", fontSize: 16}}>{score1}{tiebreak && score1 < score2 ? tiebreakElement : null}</b>
    <b style={{color: score2 > score1 ? t2Color : "#aaa", fontWeight: bold && score2 > score1 ? "normal" : "normal", fontSize: 16}}>{score2}{tiebreak && score2 < score1 ? tiebreakElement : null}</b>
  </div>
}

function SetItem({ winner, set, setIndex, t1Color, t2Color, gap }) {
  const theme = useTheme();

  if (set[0] === null && set[1] === null && set[2] === null) {
    return null;
  }

  if (setIndex === 3 && set[0] === null && set[1] === null) {
    // super tie break
    const winnerPts = Math.max(10, set[2] + 2); // minimum twee punten verschil
    const loserPts = set[2];

    return <SetItemElement bold={false} winner={winner} score1={winner == 0 ? winnerPts : loserPts} score2={winner == 1 ? winnerPts : loserPts} t1Color={t1Color} t2Color={t2Color} gap={gap} />;
  }

  return <SetItemElement winner={winner} score1={winner == 0 ? set[0] : set[1]} score2={winner == 1 ? set[0] : set[1]} tiebreak={set[1] > 0 ? set[2] : null} t1Color={t1Color} t2Color={t2Color} gap={gap} />;
}

function ScoreItem({ score, t1Color, t2Color, gap }) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (score?.forfait) {
    return <b style={{fontWeight: "normal", fontSize: 16}}>{t("recentActivity.forfeit")}</b>
  } else if (score?.wo) {
    return <b style={{fontWeight: "normal", fontSize: 16}}>{t("recentActivity.walkover")}</b>
  }

  return <Stack sx={{display: "flex", flexDirection: "row", gap: 2, alignItems: "center"}}>
    <SetItem set={score?.set1} winner={score?.winner} setIndex={1} t1Color={t1Color} t2Color={t2Color} gap={gap} />
    <SetItem set={score?.set2} winner={score?.winner} setIndex={2} t1Color={t1Color} t2Color={t2Color} gap={gap} />
    <SetItem set={score?.set3} winner={score?.winner} setIndex={3} t1Color={t1Color} t2Color={t2Color} gap={gap} />
    {score?.surrender ? <b style={{fontWeight: "normal", fontSize: 14}}> {t("recentActivity.surrender")}</b> : null}
  </Stack>;

}

function RecentActivityLine({ data, doubles, title, firstPlayerClickable=false }) {
  const theme = useTheme();

  const name = data?.my_name;
  const rank = data?.my_pts;

  const t1Color = theme.palette.primary.main;
  const t2Color = "#E8CBFE";

  const t1ColorText = theme.palette.text.primary_bold;
  const t2ColorText = theme.palette.text.primary_bold;

  const [open, setOpen] = React.useState(false);

  return (
    <ShadowBox
      sx={{
        pb: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
        //pt: title ? '12px' : 1,
        /*"&:hover": {
          backgroundColor: "action.hover",
        },*/
      }}
    >

        

        <Stack sx={{justifyContent: "space-between", width: "100%", flexDirection: "row", alignItems: "center"}}>
            
            <Stack sx={{justifyContent: "space-between", flexDirection: "column", px: 1, py: 2, gap: 1}}>
            <Box sx={{ display: "flex", alignItems: "stretch", gap: 1 }}>
              <AccentBar color={t1Color} />

              <Stack sx={{ alignItems: "flex-start", flexDirection: "column", opacity: data?.m_score?.winner == 0 ? 1 : 0.5 }}>
                <PlayerName id={firstPlayerClickable ? data.my_id : null} name={name} rank={rank} />
                {doubles ? <PlayerName id={data.p_id} name={data.p_name} rank={data.p_pts} /> : null}
              </Stack>
            </Box>

             <Box sx={{ display: "flex", alignItems: "stretch", gap: 1 }}>
                <AccentBar color={t2Color} />

                  <Stack sx={{ alignItems: "flex-start", flexDirection: "column", opacity: data?.m_score?.winner == 1 ? 1 : 0.5 }}>
                    <PlayerName id={doubles ? data.o1_id : data.o_id} name={doubles ? data.o1_name : data.o_name} rank={doubles ? data.o1_pts : data.o_pts} />
                    {doubles && data.o1_id > 0 ? <PlayerName id={data.o2_id} name={data.o2_name} rank={data.o2_pts} /> : null}
                  </Stack>
              </Box>
        </Stack>
            
            <Typography variant="h6" sx={{fontWeight: "bold", color: "#333", lineHeight: "1em", mr: 3}}>
                <ScoreItem score={data?.m_score} t1Color={t1ColorText} t2Color={t2ColorText} gap={doubles ? '30px' : '8px'} />
            </Typography>
        </Stack>

        <Divider sx={{width: "100%"}} />

        <Stack
          sx={{
            justifyContent: "space-between",
            width: "100%",
            flexDirection: "row",
            alignItems: "center",
            px: 1,
            pt: 0.5,
            pb: 0.5,
            gap: 1,
            "&:hover": {
                cursor: "pointer",
            },
          }}

          onClick={() => setOpen((prev) => !prev)}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.primary.main,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: "1.4em",
              flexShrink: 1,
              minWidth: 0,
              fontSize: 12,
              "&:hover": {
                color: theme.palette.primary.dark,
                cursor: data.url ? "pointer" : "default",
              },
            }}
            onClick={() => {
              if (data.url) {
                window.open(data.url, "_blank");
              }
            }}
          >
            {data.circuit ? `P${data.circuit} ` : ""} {data.rn?.replace(' finale', 'F').replace('poule', 'Poule').replace('finale', 'Finale')} {data.cat || data.rn ? '•' : ''} {data.title} 
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", ml: "auto", flexShrink: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#a0a0a0",
                whiteSpace: "nowrap",
                lineHeight: "1.4em",
                fontSize: 12,
                marginLeft: '10px',
              }}
            >
              {data.date ? formatDateDifference(data.date) : ""}
            </Typography>

            {open ? (
              <KeyboardArrowUpIcon sx={{ ml: 0.25, color: "#ccc" }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ ml: 0.25, color: "#ccc" }} />
            )}
          </Box>
        </Stack>

        {open && <RecentActivityH2H isSingles={!doubles} myId={data.my_id} opponentId={doubles ? data.o1_id : data.o_id} partnerId={doubles ? data.p_id : null} opponentPartnerId={doubles && data.o1_id > 0 ? data.o2_id : null} />}

    </ShadowBox>

    
  );
}

function RecentActivityH2H({isSingles, myId, opponentId, partnerId, opponentPartnerId}) {
  const data = useHead2Head(myId, opponentId, partnerId, opponentPartnerId);

    return <Box sx={{width: "100%", mt: 1, pt: 2}}>
      <Collapse in={!data} timeout="auto" unmountOnExit>
        <CircularProgress size={24} sx={{display: "block", mx: "auto", my: 2}} />
      </Collapse>

      <Collapse in={!!data} timeout="auto" unmountOnExit>
        <H2HHeader 
            isSingles={isSingles}
            showPlayers={false}
            data={data} 
        />

        <Divider sx={{my: 2, mx: 1}} />

        <H2HFullSummary data={data} isSingles={isSingles} />

      </Collapse>

    </Box>
}